/**
 * The client page's three dialogs, each a KitModal (Dialog / Confirm, style
 * B): Request changes (an optional reason: backend #131 dropped the floor),
 * the confirm Cmd Enter asks before approving, and the keyboard shortcuts on ?
 * (Pen z2Oir).
 */
import { useState } from "react";
import { Check } from "lucide-react";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import { KitModal } from "@/app/routes/dashboard/components/kit/Modal";
import { KitTextarea } from "@/app/routes/dashboard/components/kit/Textarea";
import { AGENCY, type ClientAnswer } from "../clientReviewModel";
import "./clientReview.css";

const partName = (round: ClientAnswer) =>
  round === "post" ? "the idea and its creative brief" : round === "brief" ? "the creative brief" : "the idea";

export function AskForChangesDialog({
  round,
  minimum,
  isOpen,
  busy = false,
  onOpenChange,
  onSend,
}: {
  round: ClientAnswer;
  /** An answer is already going out: Send waits for it. */
  busy?: boolean;
  /** The API's floor on a reason: 0 since backend #131. */
  minimum: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSend: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");
  const length = reason.trim().length;
  const short = Math.max(0, minimum - length);
  const ready = short === 0;
  return (
    <KitModal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) setReason("");
        onOpenChange(open);
      }}
      title="Request changes"
      description={`Say what should change in ${partName(round)}. ${AGENCY} reads it and sends a new version.`}
      modalClassName="max-w-[520px]"
    >
      <KitTextarea
        autoFocus
        dir="auto"
        rows={5}
        maxLength={2000}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        label="What should change?"
        placeholder="For example: open on the finished bowl, then show how it is made."
        hint={minimum > 0 && short > 0 ? `At least ${minimum} characters, so ${AGENCY} knows what to change. ${short} to go.` : "Optional. Your comments on the page say it too."}
        className="text-[16px] sm:text-[14px]"
      />
      <div className="mt-6 flex justify-end gap-3">
        <KitButton variant="ghost" onClick={() => onOpenChange(false)}>
          Cancel
        </KitButton>
        <KitButton
          isDisabled={!ready || busy}
          onClick={() => {
            onSend(reason.trim());
            setReason("");
          }}
        >
          Send
        </KitButton>
      </div>
    </KitModal>
  );
}

export function ApproveConfirmDialog({
  label,
  round,
  isOpen,
  busy = false,
  onOpenChange,
  onApprove,
}: {
  /** The banner's primary, "Approve". */
  label: string;
  round: ClientAnswer;
  busy?: boolean;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onApprove: () => void;
}) {
  return (
    <KitModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={`Approve ${partName(round)}?`}
      description={`${AGENCY} is told at once and moves on with ${partName(round)}.`}
      modalClassName="max-w-[440px]"
    >
      <div className="mt-2 flex justify-end gap-3">
        <KitButton variant="ghost" onClick={() => onOpenChange(false)}>
          Cancel
        </KitButton>
        <KitButton iconRight={Check} autoFocus isDisabled={busy} onClick={onApprove}>
          {label}
        </KitButton>
      </div>
    </KitModal>
  );
}

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

type Row = { label: string; keys: string[][] };
type Group = { title: string; rows: Row[] };

const COLUMNS: Group[][] = [
  [
    {
      title: "Review",
      rows: [
        { label: "Comment on the part in focus", keys: [["C"]] },
        { label: "Move to the next part", keys: [["Tab"]] },
        { label: "Next or previous comment", keys: [["J"], ["K"]] },
        { label: "Reply to the comment", keys: [["R"]] },
        { label: "Mark the comment done", keys: [["E"]] },
        { label: "Close or cancel", keys: [["Esc"]] },
      ],
    },
    {
      title: "Writing",
      rows: [
        { label: "Post", keys: [["Enter"]] },
        { label: "New line", keys: [["Shift", "Enter"]] },
      ],
    },
  ],
  [
    {
      title: "Moving around",
      rows: [
        { label: "Concept or Creative brief", keys: [["1"], ["2"]] },
        { label: "Previous or next post", keys: [["["], ["]"]] },
        { label: "Open the versions", keys: [["V"]] },
      ],
    },
    {
      title: "Deciding",
      rows: [{ label: "Approve", keys: [[isMac ? "⌘" : "Ctrl", "Enter"]] }],
    },
  ],
];

/** Keys as Tag / Default: 22 high, radius 6, the tonal fill, mono 11 capitals. */
function Keys({ keys }: { keys: string[][] }) {
  return (
    <span className="flex shrink-0 items-center gap-1">
      {keys.map((combo, index) => (
        <span key={combo.join("+")} className="flex items-center gap-1">
          {index > 0 ? <span className="px-0.5 text-[11.5px] text-co-faint">or</span> : null}
          {combo.map((key) => (
            <kbd key={key} className="co-cr-kbd">
              {key}
            </kbd>
          ))}
        </span>
      ))}
    </span>
  );
}

export function ShortcutsDialog({ isOpen, onOpenChange }: { isOpen: boolean; onOpenChange: (open: boolean) => void }) {
  return (
    <KitModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title="Keyboard shortcuts"
      description="Everything in a review works without a mouse. Press ? at any time to see this."
      modalClassName="max-w-[800px]"
    >
      <div className="grid gap-[18px] sm:grid-cols-2 sm:gap-8">
        {COLUMNS.map((column, index) => (
          <div key={index} className="flex flex-col gap-[18px]">
            {column.map((group) => (
              <section key={group.title} aria-label={group.title} className="flex flex-col gap-2">
                <h3 className="font-mono text-[10px] font-normal uppercase tracking-[0.132em] text-co-faint">{group.title}</h3>
                <ul className="flex flex-col gap-2">
                  {group.rows.map((row) => (
                    <li key={row.label} className="flex items-center gap-2.5 text-[13.5px] text-co-text2">
                      <span className="min-w-0 flex-1">{row.label}</span>
                      <Keys keys={row.keys} />
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        ))}
      </div>
      <div className="mt-6 flex justify-end">
        <KitButton variant="secondary" onClick={() => onOpenChange(false)}>
          Close
        </KitButton>
      </div>
    </KitModal>
  );
}
